import React from 'react';
import { 
    Card, CardContent, Typography, Box, FormControl, InputLabel, 
    Select, MenuItem, Button, Avatar, Stack, Chip, IconButton,
    useTheme
} from '@mui/material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';

const MemberRoster = ({ membersData, isTrainer, profile, handleUpdateSecondaryRole, handleRemoveMember }) => {
    const theme = useTheme();
    if (!membersData?.members) return null;

    return (
        <Card sx={{ 
            borderRadius: "12px", 
            border: '1px solid', 
            borderColor: 'divider',
            background: 'rgba(255,255,255,0.01)',
            backdropFilter: 'blur(20px)',
            boxShadow: 'none'
        }}>
            <CardContent sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, p: 4 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                            Kader <span style={{ color: theme.palette.primary.main }}>Verwaltung</span>
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {membersData.members.length} Aktive Mitglieder
                        </Typography>
                    </Box>
                    <AdminPanelSettingsRoundedIcon sx={{ color: 'primary.main', fontSize: 32, opacity: 0.8 }} />
                </Stack>

                <Stack spacing={2}>
                    {membersData.members.map((m, idx) => (
                        <Box 
                            key={m.id} 
                            sx={{ 
                                width: '100%',
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                p: 2.5, 
                                border: '1px solid', 
                                borderColor: 'divider', 
                                borderRadius: "12px",
                                transition: 'all 0.3s ease',
                                bgcolor: 'rgba(255,255,255,0.02)',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'rgba(190, 242, 100, 0.03)',
                                    transform: 'translateX(4px)'
                                }
                            }}
                        >
                            <Stack direction="row" spacing={2.5} alignItems="center">
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                        {m.first_name} {m.last_name}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {m.team_role}
                                        </Typography>
                                        {m.sekundaer_rolle && (
                                            (m.sekundaer_rolle || '').toString().split(',').map((r, i) => r.trim()).filter(Boolean).map((r, i) => (
                                                <Chip key={i}
                                                    label={r}
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 900,
                                                        bgcolor: 'rgba(255,255,255,0.08)',
                                                        border: '1px solid rgba(255,255,255,0.1)',
                                                        ml: i === 0 ? 1 : 0.5
                                                    }}
                                                />
                                            ))
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>

                            <Stack direction="row" spacing={2} alignItems="center">
                                {isTrainer && m.id !== profile?.user?.id && (
                                    <FormControl size="small" sx={{ minWidth: 180 }}>
                                        <InputLabel sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Rollen</InputLabel>
                                        <Select
                                            multiple
                                            value={ (m.sekundaer_rolle && m.sekundaer_rolle.toString()) ? m.sekundaer_rolle.toString().split(',').map(s => s.trim()).filter(Boolean) : [] }
                                            label="Rollen"
                                            onChange={(e) => handleUpdateSecondaryRole(m.id, (Array.isArray(e.target.value) ? e.target.value.join(',') : e.target.value))}
                                            sx={{ 
                                                borderRadius: "12px",
                                                fontWeight: 700,
                                                fontSize: '0.85rem',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' }
                                            }}
                                        >
                                            <MenuItem value="kassenwart" sx={{ fontWeight: 700 }}>Kassenwart</MenuItem>
                                            <MenuItem value="statistikwart" sx={{ fontWeight: 700 }}>Statistikwart</MenuItem>
                                            <MenuItem value="instateam" sx={{ fontWeight: 700 }}>Instateam</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                                
                                {m.id !== profile?.user?.id && (
                                    <IconButton 
                                        color="error" 
                                        onClick={() => handleRemoveMember(m.id, `${m.first_name} ${m.last_name}`)}
                                        sx={{ 
                                            bgcolor: 'rgba(255, 0, 0, 0.05)',
                                            borderRadius: "12px",
                                            '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.15)' }
                                        }}
                                    >
                                        <DeleteRoundedIcon fontSize="small" />
                                    </IconButton>
                                )}
                                {m.id === profile?.user?.id && (
                                    <VerifiedUserRoundedIcon sx={{ color: 'primary.main', opacity: 0.5 }} />
                                )}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default MemberRoster;
